"use client";

import Link from "next/link";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useMyPracticeMemberships } from "@/hooks/use-practices";
import type { CurrentUser } from "@/lib/feed/types";
import { skipIntroductionChecklist } from "@/lib/onboarding/api";
import { queryKeys } from "@/lib/query-keys";
import { getErrorMessage } from "@/lib/utils";

type IntroductionChecklistProps = {
  enabled: boolean;
  communityCount: number;
};

export function IntroductionChecklist({
  enabled,
  communityCount,
}: IntroductionChecklistProps) {
  const currentUserQuery = useCurrentUser();
  const practicesQuery = useMyPracticeMemberships(enabled);
  const queryClient = useQueryClient();
  const user = currentUserQuery.data;

  const skipMutation = useMutation({
    mutationFn: skipIntroductionChecklist,
    onSuccess: () => {
      queryClient.setQueryData<CurrentUser | null>(
        queryKeys.currentUser,
        (current) =>
          current
            ? {
                ...current,
                introductionServicesViewed: true,
                introductionSettingsCompleted: true,
                introductionSkipped: true,
              }
            : current,
      );
    },
  });

  const belongsToPractice = (practicesQuery.data ?? []).some(
    (practice) => practice.membershipStatus === "active",
  );
  const belongsToCommunity = communityCount > 0;

  const tasks = [
    {
      label: "Praxisba tartozás",
      href: "/practices",
      completed: belongsToPractice,
    },
    {
      label: "Közösségbe csatlakozás",
      href: "/communities",
      completed: belongsToCommunity,
    },
    {
      label: "Szolgáltatások megtekintése",
      href: "/services",
      completed: user?.introductionServicesViewed ?? false,
    },
    {
      label: "Beállítások kitöltése",
      href: "/settings",
      completed: user?.introductionSettingsCompleted ?? false,
    },
  ];

  const completedCount = tasks.filter((task) => task.completed).length;
  const allCompleted = completedCount === tasks.length;

  if (
    !enabled ||
    !user?.onboardingCompleted ||
    user.introductionSkipped ||
    user.introductionExpired ||
    allCompleted
  ) {
    return null;
  }

  return (
    <section className="shadow-surface overflow-hidden rounded-2xl border border-blue-100 bg-card">
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50/70 to-white px-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
            <ClipboardCheck className="size-4.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900">Bevezetés</h2>
              <span className="text-xs font-semibold text-primary">
                {completedCount}/{tasks.length}
              </span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">
              Végezze el az első lépéseket a PraxisTárs használatához.
            </p>
          </div>
        </div>

        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-blue-100">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${(completedCount / tasks.length) * 100}%` }}
          />
        </div>
      </div>

      <ul className="divide-y divide-slate-100 px-3">
        {tasks.map((task) => (
          <li key={task.href}>
            <Link
              href={task.href}
              className="group flex items-center gap-2.5 rounded-lg px-1 py-2.5 transition-colors hover:bg-slate-50"
            >
              {task.completed ? (
                <CheckCircle2 className="size-4.5 shrink-0 text-emerald-600" />
              ) : (
                <Circle className="size-4.5 shrink-0 text-slate-300 transition-colors group-hover:text-primary" />
              )}
              <span
                className={`min-w-0 flex-1 text-xs font-medium ${
                  task.completed
                    ? "text-slate-400 line-through"
                    : "text-slate-700"
                }`}
              >
                {task.label}
              </span>
              {task.completed ? (
                <Check className="size-3.5 shrink-0 text-emerald-600" />
              ) : (
                <ChevronRight className="size-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              )}
            </Link>
          </li>
        ))}
      </ul>

      <div className="border-t border-slate-100 px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full rounded-full text-xs text-muted-foreground"
          disabled={skipMutation.isPending}
          onClick={() => skipMutation.mutate()}
        >
          {skipMutation.isPending ? "Lezárás..." : "Bevezetés kihagyása"}
        </Button>
        {skipMutation.isError ? (
          <p className="px-1 pb-1 text-xs text-destructive">
            {getErrorMessage(skipMutation.error)}
          </p>
        ) : null}
      </div>
    </section>
  );
}
